import "@assets/css/bootstrap-grid.css";
import SchemasField from "@/components/json-ld-schemas/SchemasField";
import {
  getJsonLdDefaults,
  validateJsonLdSchemas,
} from "@/components/json-ld-schemas/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { baseURL } from "@services/index.services";
import { useQueryClient } from "@tanstack/react-query";
import { FormSkeleton } from "@ui/external/components/skeleton";
import DragTEst from "@ui/internal/components/drag-test/DragContainer";
import { getInputField } from "@utils/function/index.function";
import {
  changeSymbolstoDash,
  makeSlug,
  showToast,
  throwErrorAlert,
} from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { FormField } from "@utils/interface/form.interface";
import {
  SuccessStoriesFormData,
  SuccessStoriesSchema,
} from "@utils/interface/form.schema";
import { IndividualQuery, METHODS } from "@utils/interface/index.interface";
import { SuccessStoryData } from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import React, { useEffect, useMemo } from "react";
import { useFieldArray, useForm } from "react-hook-form";

type ItemDescription = {
  type: "description";
  id: number;
  question: string;
  answer: string;
};

type ItemQuotation = {
  type: "quotation";
  id: number;
  description: string;
};

type ItemImage = {
  type: "image";
  id: number;
  label: string;
  image: File | ""; // or string if you store URL or base64
};

export type Format = {
  items: (ItemDescription | ItemQuotation | ItemImage)[];
};
const SuccessForm: React.FC = () => {
  const queryClient = useQueryClient();
  const { handleAction, searchParams } = useHandleAction({});
  const pageItem = searchParams.get("page-item");
  const pageMode = searchParams.get("page");
  const pathname = usePathname();

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<SuccessStoriesFormData>({
    mode: "onChange",
    resolver: zodResolver(SuccessStoriesSchema),
    defaultValues: {
      title: pageMode === "add" ? (searchParams.get("search") as string) : "",
      items: [
        {
          type: "description",
          question: "",
          answer: "",
        },
      ],
      json_ld: [],
      working_at: "",
      working_position: "",
      course_link: "",
    },
  });
  useEffect(() => {
    if (pageMode === "add") {
      setValue("slug", changeSymbolstoDash(makeSlug(watch("title"))));
    }
  }, [watch("title")]);
  console.log(errors, "//errors//");
  const { fields, append, move, remove } = useFieldArray({
    name: "items",
    control,
  });
  /* set default value */
  const { setIsRetrive }: any = usePending();
  const { setIsFormPending } = useSelectionStore();

  const { data, isPending: fetchPending } = useListData<
    IndividualQuery<SuccessStoryData>
  >({
    endpoint: endpoint["successstory"],
    id: pageItem || "",
    key: "successstory" + pageItem,
    enabled: pageItem ? true : false,
  });

  const { mutate, isPending } = usePostData();

  useEffect(() => {
    setIsRetrive(pageItem ? fetchPending : false);

    if (searchParams.get("page") == "add") {
      return reset({ json_ld: [] } as any);
    }
    // console.log(data?.data, "EMEMMEMEMEMEME");
    if (data && searchParams.get("page") !== "add") {
      return reset({
        featured_image: data?.data?.featured_image,
        // @ts-ignore
        items: Array.isArray(data?.data?.description)
          ? data?.data?.description?.map((item) => {
              if ("url" in item) {
                const { url, ...rest } = item;
                return {
                  ...rest,
                  image: url,
                };
              }
              return item;
            })
          : [],
        student: [
          {
            label: data?.data?.student?.user?.full_name,
            value: data?.data?.student?.id,
          },
        ],
        team_member: [
          {
            label: data?.data?.team_member?.user?.email,
            value: data?.data?.team_member?.id,
          },
        ],
        testimonial_video: data?.data?.testimonial_video,
        feature_quotation: data?.data?.feature_quotation || undefined,
        title: data?.data?.title,
        slug: data?.data?.slug,
        header_code: data?.data?.header_code || "",
        footer_code: data?.data?.footer_code || "",
        json_ld: getJsonLdDefaults(data?.data?.json_ld),
        working_at: data?.data?.working_at || "",
        working_position: data?.data?.working_position || "",
        course_link: data?.data?.course_link || "",
      });
    }
  }, [data, reset, setIsRetrive]);

  async function uploadcImage(file: File) {
    const formData = new FormData();
    formData.append("image", file);
    const res = await fetch(baseURL + endpoint["uploadimage"], {
      method: "POST",
      body: formData,
    });

    if (!res.ok) throw new Error("Upload failed");
    const data = await res.json();
    return data.image;
  }

  /* mutate action */
  const handleMutate = (data: any) => {
    setIsFormPending(true);
    console.log("trigger ok", data);

    if (pageItem) {
      data = {
        ...data,
        id: pageItem,
      };
    }
    //   if (
    //     !(
    //       data.featured_image instanceof File ||
    //       data.featured_image instanceof Blob
    //     )
    //   )
    //     delete data.featured_image;
    // }
    // if (
    //   !(
    //     data.testimonial_video instanceof File ||
    //     data.testimonial_video instanceof Blob
    //   )
    // )
    //   delete data.testimonial_video;

    return {
      data,
      endpoint: endpoint["successstory"],
      method: pageItem ? METHODS.PUT : METHODS.POST,
    };
  };
  const handleSuccess = async ({ status }: { data: any; status: number }) => {
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });
      setIsFormPending(false);
      showToast({
        description: `Success Story ${status === 200 ? "Updated" : "Created"}`,
        type: "success",
        title: "Success",
      });
      handleAction({ type: "page-close" });
    } else {
      return new Error("Unknown API Error");
    }
  };
  const handleError = async (error: any) => {
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }
    setIsFormPending(false);
    throwErrorAlert(error);
  };

  const onSubmit = async (data: SuccessStoriesFormData) => {
    clearErrors("json_ld");
    const jsonLdResult = validateJsonLdSchemas(
      (data as any).json_ld || [],
      setError as any,
    );

    if (!jsonLdResult.isValid) {
      showToast({
        title: "Invalid JSON-LD",
        description: `Fix the highlighted schema block (#${jsonLdResult.firstInvalidIndex + 1}) before saving.`,
        type: "error",
      });
      return;
    }

    const processedData = await (async function replaceImagesWithLinks(data) {
      const updatedItems = await Promise.all(
        (data.items ?? []).map(async (item: ItemImage) => {
          if (item.type === "image" && item.image instanceof File) {
            try {
              const images = await uploadcImage(item.image);
              const { image, ...rest } = item;
              return {
                ...rest,
                image: images, // Add uploaded image URL
              };
            } catch (e) {
              console.error(`Image upload failed for ${item.label}`, e);
              return item;
            }
          }
          return item;
        })
      );

      return {
        ...data,
        items: updatedItems,
      };
    })(data);

    // Final structure before sending
    const finalData = {
      ...processedData,
      description: JSON.stringify(processedData.items),
      student: processedData?.student?.[0]?.value,
      team_member: processedData?.team_member?.[0]?.value,
      json_ld: jsonLdResult.normalizedSchemas,
      working_at: processedData.working_at ?? "",
      working_position: processedData.working_position ?? "",
      course_link: processedData.course_link ?? "",
    };
    // @ts-ignore
    delete finalData.items;

    mutate(handleMutate(finalData), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  const formSchema: FormField[] = useMemo(
    () => [
      {
        name: "title",
        label: "Title",
        colSpan: 12,
        type: "text",
        placeholder: "Enter Title",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "slug",
        label: "Slug",
        colSpan: 12,
        type: "text",
        placeholder: "Enter Slug",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "team_member",
        label: "Team Member",
        colSpan: 6,
        type: "async",
        endpoint: endpoint["team-member"],
        placeholder: "Select Team Member",
        format: {
          key: "full_name",
          value: "id",
        },
        control: control,
        isMulti: false,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full !w-full`,
        visable: true,
      },
      {
        name: "student",
        label: "Student",
        colSpan: 6,

        placeholder: "Select Student",
        control: control,
        isMulti: false,
        setValue: setValue,
        type: "async",
        endpoint: endpoint["studentselect"],

        format: { key: "full_name", value: "id" },

        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "feature_quotation",
        label: "Feature quotation",
        colSpan: 12,
        type: "editor",
        showLength: true,
        placeholder: "Enter feature_quotation",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "working_at",
        label: "Working At",
        colSpan: 6,
        type: "text",
        placeholder: "Enter Working At",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "working_position",
        label: "Position",
        colSpan: 6,
        type: "text",
        placeholder: "Enter Position",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "course_link",
        label: "Course Link",
        colSpan: 12,
        type: "text",
        placeholder: "Enter Course Link",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
    ],
    [control, errors, setValue]
  );
  const sideformSchema: FormField[] = useMemo(
    () => [
      {
        name: "featured_image",
        label: "Feature Image",
        colSpan: 12,
        type: "cropper",
        placeholder: "Enter Title",
        control: control,
        cropperWidthPlaceholder: 415,
        cropperHeightPlaceholder: 571,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "testimonial_video",
        label: "Feature Video",
        colSpan: 12,
        upload_type: "video",
        type: "cropper",
        placeholder: "Enter Title",
        control: control,
        setValue: setValue,
        errors,
        cropperWidthPlaceholder: 415,
        cropperHeightPlaceholder: 571,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
      name: "header_code",
      label: "Header Code ",
      colSpan: 12,
      type: "textarea",
      placeholder: "Description Here",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },
    {
      name: "footer_code",
      label: "Footer Code ",
      colSpan: 12,
      type: "textarea",
      placeholder: "Description Here",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },
    ],
    [control, errors, setValue, isPending]
  );
  if (fetchPending && pageItem)
    return (
      <>
        <FormSkeleton />
      </>
    );

  return (
    <>
      <form
        id={pathname}
        onSubmit={handleSubmit(onSubmit)}
        className="row w-full"
      >
        <div className="col-md-8">
          <div className="row">
            {formSchema.map(
              (field, index) =>
                (field.visable || field.visable == undefined) && (
                  <div
                    key={index}
                    className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
                  >
                    {getInputField({ ...field })}
                  </div>
                )
            )}
          </div>
          <DragTEst
            append={append}
            control={control}
            fields={fields}
            remove={remove}
            getValues={getValues}
            move={move}
            setValue={setValue}
            watch={watch}
          />
          <div className="col-12 lg:px-[12px] px-[8px] mt-5">
            <div className="border rounded-md p-4 bg-white">
              <h2 className="text-lg font-semibold mb-4">SEO Schemas</h2>
              <SchemasField control={control} errors={errors} name="json_ld" />
            </div>
          </div>
          {/* <DragContainer /> */}
        </div>
        <div className="col-md-4">
          {sideformSchema.map(
            (field, index) =>
              (field.visable || field.visable == undefined) && (
                <div
                  key={index}
                  className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
                >
                  {getInputField({ ...field })}
                </div>
              )
          )}
        </div>
      </form>
    </>
  );
};

export default SuccessForm;
